import React from 'react';

import { Check, DollarSign, Minus } from 'lucide-react';

import Tooltip from 'common/common/Tooltip';
import { H4, Span } from 'common/ui/Text';
import numberWithCommas from 'common/util/numberWithCommas';
import styles from 'css-module/components/www/_PricingV4.module.scss';

import PricingV4Plans, { type Plan, getPricePoint } from './PricingV4Plans';

import type { Timeframe } from './Timeframe';

type Props = {
  trackedUsers: number;
  timeframe: Timeframe;
};
export default function PricingV4Grid({ timeframe, trackedUsers }: Props) {
  return (
    <div className={styles.pricingV4Grid}>
      <div className={styles.gridContainer}>
        <GridHeadingRow timeframe={timeframe} trackedUsers={trackedUsers} />
        <div className={styles.gridBody}>
          {Object.keys(SectionItems).map((sectionKey, i) => {
            const section = SectionItems[sectionKey as keyof typeof SectionItems];
            return (
              <div key={i} className={styles.gridSection}>
                <GridSectionHeadingRow label={section.label} />
                {Object.keys(section).map((itemKey, j) => {
                  if (itemKey === 'label') {
                    return null;
                  }
                  return (
                    <GridSectionRow
                      key={j}
                      item={itemKey as keyof Omit<typeof section, 'label'>}
                      section={sectionKey as keyof typeof SectionItems}
                      trackedUsers={trackedUsers}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type GridHeadingRowProps = {
  timeframe: Timeframe;
  trackedUsers: number;
};
function GridHeadingRow({ timeframe, trackedUsers }: GridHeadingRowProps) {
  return (
    <div className={styles.gridHeadingRow}>
      <GridHeadingCell />
      <GridHeadingCell
        plan={PricingV4Plans.free}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
      <GridHeadingCell
        plan={PricingV4Plans.core}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
      <GridHeadingCell
        plan={PricingV4Plans.pro}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
      <GridHeadingCell
        plan={PricingV4Plans.business}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
    </div>
  );
}

type GridHeadingCellProps =
  | Record<string, never>
  | {
      plan: Plan;
      timeframe: Timeframe;
      trackedUsers: number;
    };
function GridHeadingCell({ plan, timeframe, trackedUsers }: GridHeadingCellProps) {
  if (!plan) {
    return <div className={styles.gridHeadingCell} />;
  }

  let priceAmount;
  let pricePeriod;
  if (plan.pricePoints) {
    const pricePoint =
      getPricePoint(plan, trackedUsers) || plan.pricePoints[plan.pricePoints.length - 1];
    priceAmount = `$${numberWithCommas(pricePoint[timeframe])}`;
    pricePeriod = '\u00a0/\u00a0mo';
  } else {
    priceAmount = 'Custom';
    pricePeriod = '';
  }
  return (
    <div className={styles.gridHeadingCell}>
      <H4 variant="headingLg">{plan.name}</H4>
      <div className={styles.gridPrice}>
        <Span className={styles.gridPriceAmount} fontWeight="semibold" variant="bodyLg">
          {priceAmount}
        </Span>
        <Span className={styles.gridPricePeriod} fontWeight="semibold" variant="bodyLg">
          {pricePeriod}
        </Span>
      </div>
      {timeframe === 'yearly' && (
        <Span className={styles.gridPriceTimeframe} variant="bodySm">
          billed yearly
        </Span>
      )}
    </div>
  );
}

type GridSectionHeadingRowProps = {
  label: string;
};
function GridSectionHeadingRow({ label }: GridSectionHeadingRowProps) {
  return (
    <div className={styles.gridSectionHeadingRow}>
      <H4 variant="headingSm">{label}</H4>
    </div>
  );
}

type GridKey = keyof Plan['grid'];
type GridRowProps<T extends GridKey> = {
  section: T;
  trackedUsers: number;
  item: keyof Plan['grid'][T];
};
function GridSectionRow<T extends GridKey>({ item, section, trackedUsers }: GridRowProps<T>) {
  const values = [
    PricingV4Plans.free,
    PricingV4Plans.core,
    PricingV4Plans.pro,
    PricingV4Plans.business,
  ].map((plan) => {
    return plan.grid[section][item] as
      | string
      | number
      | boolean
      | null
      | ((trackedUsers: number) => string);
  });
  const itemProps = values.map((value) => ({
    ...(item === 'trackedUsers' &&
      typeof value === 'function' && {
        trackedUsers,
      }),
  }));
  return (
    <div className={styles.gridSectionRow}>
      <GridSectionRowItem
        isHeader={true}
        link={SectionItems[section][item].link}
        subvalue={SectionItems[section][item].sublabel}
        tooltip={SectionItems[section][item].tooltip}
        value={SectionItems[section][item].label}
      />
      <GridSectionRowItem value={values[0]} {...itemProps[0]} />
      <GridSectionRowItem value={values[1]} {...itemProps[1]} />
      <GridSectionRowItem value={values[2]} {...itemProps[2]} />
      <GridSectionRowItem value={values[3]} {...itemProps[3]} />
    </div>
  );
}

type GridSectionRowItemProps = {
  isHeader?: boolean;
  link?: Link;
  subvalue?: string;
  tooltip?: string;
  trackedUsers?: number;
  value: null | boolean | number | string | ((trackedUsers: number) => string);
};
function GridSectionRowItem({
  isHeader,
  link,
  subvalue,
  tooltip,
  trackedUsers,
  value,
}: GridSectionRowItemProps) {
  if (typeof value === 'function') {
    if (typeof trackedUsers !== 'number') {
      return null;
    }
    return (
      <div className={styles.gridSectionRowItem}>
        <Span variant="bodyMd">{value(trackedUsers)}</Span>
      </div>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <div className={styles.gridSectionRowItem}>
        {value ? (
          <Check className={styles.gridCheck} size={20} />
        ) : (
          <Minus className={styles.gridMinus} size={20} />
        )}
      </div>
    );
  } else if (typeof value === 'number') {
    return (
      <div className={styles.gridSectionRowItem}>
        <Span variant="bodyMd">{value}</Span>
      </div>
    );
  } else if (value === null) {
    return (
      <div className={styles.gridSectionRowItem}>
        <Span variant="bodyMd">Unlimited</Span>
      </div>
    );
  } else if (value === 'upsell') {
    return (
      <div className={styles.gridSectionRowItem}>
        <DollarSign className={styles.gridDollarSign} size={20} />
      </div>
    );
  }

  const valueText = (
    <Span className={styles.gridSectionRowItemValue} variant="bodyMd">
      {value}
    </Span>
  );

  return (
    <div
      className={styles.gridSectionRowItem}
      data-has-tooltip={!!tooltip}
      data-is-row-header={isHeader}>
      {tooltip ? <Tooltip value={tooltip}>{valueText}</Tooltip> : valueText}
      {subvalue && (
        <Span className={styles.sublabel} color="gray110" variant="bodySm">
          {subvalue}
        </Span>
      )}
      {link && (
        <a
          className={styles.gridSectionRowHeaderLink}
          href={link.href}
          target="_blank"
          rel="noopener">
          <Span variant="bodySm">{link.label}</Span>
        </a>
      )}
    </div>
  );
}

type Link = {
  href: string;
  label: string;
};
const SectionItems: {
  [K in GridKey]: {
    [P in keyof Plan['grid'][K]]: {
      label: string;
      sublabel?: string;
      link?: Link;
      tooltip?: string;
    };
  } & {
    label: string;
  };
} = {
  userTypes: {
    label: 'User Types',
    trackedUsers: {
      label: 'Tracked users',
      tooltip: 'Any user associated with feedback',
    },
    managers: {
      label: 'Managers',
      tooltip: 'Canny admins that manage feedback and roadmaps',
    },
    contributors: {
      label: 'Contributors',
      tooltip: 'Canny admins that contribute feedback',
    },
  },
  feedbackPortal: {
    label: 'Feedback Portal',
    posts: {
      label: 'Posts',
      tooltip: 'Individual feedback items',
    },
    boards: {
      label: 'Boards',
      tooltip: 'Posts are organized into Boards',
    },
    core: {
      label: 'Base features',
      sublabel:
        'Status updates, single sign-on, admin reports, vote-on-behalf, tags, widget, categories, owners, and more',
      link: {
        href: 'https://canny.io/features',
        label: 'See features page',
      },
    },
    changelog: {
      label: 'Changelog',
      tooltip: 'Release notes and product announcements',
    },
    privateBoards: {
      label: 'Private boards',
      tooltip: 'Collect and share feedback privately with your team',
    },
    customDomains: {
      label: 'Custom domains',
      tooltip: 'Use your domain like ideas.company.com',
    },
    contentTranslations: {
      label: 'Content translations',
      tooltip: 'Automatically translate user-generated content',
    },
    customStatuses: {
      label: 'Custom statuses',
      tooltip: 'Add, remove, and update statuses',
    },
    advancedPortalPrivacy: {
      label: 'Advanced portal privacy',
      sublabel:
        'Board and changelog access, private comments, disable new post creation, anonymized boards, hide vote counts',
    },
    removeBranding: {
      label: 'Remove branding',
      tooltip: 'Remove "Powered by\u00a0Canny"',
    },
    emailWhitelabel: {
      label: 'Email whitelabel',
      tooltip: 'Send Canny emails from your domain',
    },
  },
  feedbackManagement: {
    label: 'Feedback Management',
    roadmaps: {
      label: 'Roadmaps',
      tooltip: 'Score and prioritize feedback',
    },
    apiWebhooks: {
      label: 'API & webhooks',
    },
    internalComments: {
      label: 'Internal comments',
      tooltip: 'Admin-only comments on posts',
    },
    customFields: {
      label: 'Custom post fields',
      tooltip: 'Create and manage your own data fields',
    },
    userSegmentation: {
      label: 'User segmentation',
      tooltip: 'Filter feedback to user groups you care about e.g. paying customers',
    },
    automations: {
      label: 'Automation rules',
      tooltip: 'Build your own feedback workflows',
    },
    adminRoles: {
      label: 'Custom admin roles',
      tooltip: 'Customize admin permissions',
    },
  },
  autopilot: {
    label: 'Autopilot AI',
    autopilotSources: {
      label: 'Autopilot sources',
      sublabel: 'Intercom, Zendesk, Freshdesk, Gong, Zoom, and more',
    },
    capture: {
      label: 'Feedback discovery',
      tooltip: 'Automatically capture feedback from customer conversations',
    },
    dedupe: {
      label: 'Feedback deduplication',
      tooltip: 'Automatically detect and merge similar ideas',
    },
    smartReplies: {
      label: 'Smart replies',
      tooltip: 'Automated replies on new posts to collect more details',
    },
    commentSummaries: {
      label: 'Comment summaries',
      tooltip: 'Summarize user comments using AI',
    },
  },
  integrations: {
    label: 'Integrations',
    capture: {
      label: 'Feedback capture',
      sublabel: 'Intercom, Zendesk, Slack, Chrome\u00A0Extension',
      tooltip: 'Capture feedback where you find it',
    },
    notifications: {
      label: 'Notifications',
      sublabel: 'Slack, Microsoft Teams, Discord',
      tooltip: 'Get notified about activity in Canny',
    },
    segment: {
      label: 'Segment',
    },
    projectManagement: {
      label: 'Project management',
      sublabel: 'Jira, ClickUp, Linear, Asana, GitHub, Azure DevOps',
      tooltip: 'Sync feedback with tasks',
    },
    zapier: {
      label: 'Zapier',
    },
    auth: {
      label: 'Auth & SSO',
      sublabel: 'Okta, OIDC, Entra ID, OneLogin',
      tooltip: "Manage your team's access to Canny",
    },
    crm: {
      label: 'CRM',
      sublabel: 'HubSpot, Salesforce',
      tooltip: 'Track customer feedback and sync in user data',
    },
  },
  compliance: {
    label: 'Compliance',
    dataPrivacy: {
      label: 'Data Privacy',
      sublabel: 'CCPA, GDPR',
    },
    soc2: {
      label: 'SOC 2',
    },
    invoice: {
      label: 'Pay by invoice',
    },
    securityReview: {
      label: 'Security review',
    },
  },
};
